import React from 'react';

import batteryIcon from '@assets/img/icons/battery.svg';
import cosmeticsIcon from '@assets/img/icons/cosmetics.svg';
import dangerousIcon from '@assets/img/icons/dangerous.svg';
import foodIcon from '@assets/img/icons/food.svg';
import intellectualIcon from '@assets/img/icons/intellectual.svg';
import kitchenwareIcon from '@assets/img/icons/kitchenware.svg';
import livingThingsIcon from '@assets/img/icons/living-things.svg';
import pharmaceuticalIcon from '@assets/img/icons/pharmaceutical.svg';
import tablewareIcon from '@assets/img/icons/tableware.svg';
import toysIcon from '@assets/img/icons/toys.svg';
import { Layout } from '@components/Layout';
import { bookingCautionsStrings } from '@data/bookingInquiry';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';

const strings = createStrings({
  ja: {
    title: '荷物の注意事項',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    padding: 20,
  },
  alertIconTitle: {
    marginRight: 10,
  },
  alertBanner: {
    ...common.smallerText,
    ...common.fontWeight600,
    color: theme.palette.common.white,
    backgroundColor: '#F05F66',
    textAlign: 'center',
    paddingTop: 6,
    paddingBottom: 6,
    marginBottom: 10,
    filter: 'drop-shadow(0px 2px 8px rgba(200, 201, 204, 0.5))',
  },
  importantCargos: {
    ...common.flexColumn,
    borderRadius: 2,
    border: '4px solid #00647D',
    boxShadow: '0px 2px 8px rgba(200, 201, 204, 0.5)',
    overflow: 'hidden',
  },
  importantCargosTitle: {
    ...common.flexCenter,
    ...common.smallerText,
    ...common.fontWeight600,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    paddingTop: 12,
    paddingBottom: 12,
  },
  importantCargosContent: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(3rem, 1fr))',
    rowGap: 20,
    columnGap: 15,
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 20,
    paddingBottom: 20,
  },
  cargoItem: {
    ...common.flexColumn,
    ...common.exSmallerText,
    alignItems: 'center',
    color: theme.palette.common.black,
    maxWidth: '100%',
    margin: '0 auto',
    whiteSpace: 'pre',
    overflow: 'visible',
    wordWrap: 'normal',
  },
  cargoImg: {
    maxWidth: '100%',
  },
  inputNotesPrompt: {
    ...common.smallerText,
    ...common.fontWeight800,
    color: theme.palette.common.black,
    marginTop: 8,
    lineHeight: 2,
    '& span': {
      color: theme.palette.primary.main,
    },
  },
  noteItem: {
    ...common.smallerText,
    color: theme.palette.grey[800],
    lineHeight: 1.25,
    marginBottom: '0.5rem',
    position: 'relative',
    '&::before': {
      content: '""',
      height: 5,
      width: 5,
      position: 'absolute',
      top: 5,
      left: -15,
      backgroundColor: theme.palette.primary.main,
    },
  },
  noteItemContent: {
    ...common.exSmallerText,
    color: theme.palette.grey[700],
    marginTop: '0.25rem',
  },
  inputNotes: {
    ...common.flexColumn,
    marginTop: 16,
    paddingLeft: 17,
    rowGap: '0.8rem',
  },
}));

interface CargoItemProps {
  image: string;
  title: React.ReactNode;
}

const CargoItem: React.FC<CargoItemProps> = (props) => {
  const { image, title } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.cargoItem}>
      <object data={image} type="image/svg+xml" className={classes.cargoImg} />
      <span style={{ marginTop: 8, textAlign: 'center' }}>{title}</span>
    </div>
  );
};

interface NoteItemProps {
  title: string;
  content?: string;
}

const NoteItem: React.FC<NoteItemProps> = (props) => {
  const { title, content } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.noteItem}>
      <div className={classes.fontWeight600Important}>{title}</div>
      {content && <div className={classes.noteItemContent}>{content}</div>}
    </div>
  );
};

export const MobileCautions: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <Layout
      title={strings.title}
      breadcrumbs={['/utilities/Dashboard', '/utilities/BookingInquiry/MobileCautions']}
    >
      <div className={classes.container}>
        <div className={classes.alertBanner}>{bookingCautionsStrings.alertBanner}</div>

        <div className={classes.importantCargos}>
          <div className={classes.importantCargosTitle}>
            {bookingCautionsStrings.importantCargosTitle}
          </div>

          <div className={classes.importantCargosContent}>
            <CargoItem image={cosmeticsIcon} title={bookingCautionsStrings.cosmetics} />
            <CargoItem image={batteryIcon} title={bookingCautionsStrings.battery} />
            <CargoItem image={dangerousIcon} title={bookingCautionsStrings.dangerous} />
            <CargoItem image={pharmaceuticalIcon} title={bookingCautionsStrings.pharmaceutical} />
            <CargoItem image={tablewareIcon} title={bookingCautionsStrings.tableware} />
            <CargoItem image={kitchenwareIcon} title={bookingCautionsStrings.kitchenware} />
            <CargoItem image={foodIcon} title={bookingCautionsStrings.food} />
            <CargoItem image={livingThingsIcon} title={bookingCautionsStrings.livingThings} />
            <CargoItem image={toysIcon} title={bookingCautionsStrings.toys()} />
            <CargoItem
              image={intellectualIcon}
              title={bookingCautionsStrings.intellecturalMobile()}
            />
          </div>
        </div>

        <div className={classes.inputNotesPrompt}>{bookingCautionsStrings.inputNotesPrompt()}</div>

        <div className={classes.inputNotes}>
          {bookingCautionsStrings.notes().map(([title, content]) => (
            <NoteItem key={title} title={title} content={content} />
          ))}
        </div>
      </div>
    </Layout>
  );
};
